// tabs/CompetitorsTab.jsx
// Competitors analysis tab component showing direct competitors and market insights
// Shows only real data from analysis with proper empty states

import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { HelpCircle, Users, Target, Globe, DollarSign } from 'lucide-react';
import HelpIcon from '../shared/HelpIcon';
import { formatRevenue } from '../shared/constants';

/**
 * Competitors analysis tab component
 * @param {Object} props - Component props  
 * @param {Object} props.analysis - Complete competitive analysis data (dual-source)
 * @returns {JSX.Element} Competitors tab component
 */
const CompetitorsTab = ({ analysis, onGameClick }) => {
    const [showAllCompetitors, setShowAllCompetitors] = useState(false);

    // Extract from gamalyticsNetwork (new structure) or fallback to old structure
    const gamalyticsData = analysis?.gamalyticsNetwork || analysis || {};

    // Safe extraction with defaults
    const { competitors = {} } = gamalyticsData;
    const direct = competitors?.direct || {};
    const directCompetitors = direct?.competitors || [];
    const landscape = competitors?.landscape || {};
    const threats = competitors?.threats || {};
    const opportunities = competitors?.opportunities || [];

    // Use only real data
    const allCompetitors = directCompetitors;

    // Sort by relevance (link value) from highest to lowest if we have data
    const sortedCompetitors = allCompetitors.length > 0 
        ? [...allCompetitors].sort((a, b) => (b.link || 0) - (a.link || 0))
        : [];

    // Show top 3 initially, or all if showAllCompetitors is true
    const displayedCompetitors = showAllCompetitors ? sortedCompetitors : sortedCompetitors.slice(0, 3);
    const hasMoreCompetitors = sortedCompetitors.length > 3;

    // Calculate market insights only if we have real data
    const totalMarketRevenue = sortedCompetitors.reduce((sum, comp) => sum + (comp.revenue || 0), 0);
    const averagePrice = sortedCompetitors.length > 0 
        ? sortedCompetitors.reduce((sum, comp) => sum + (comp.price || 0), 0) / sortedCompetitors.length 
        : 0;

    return (
        <div className="p-6 space-y-6">
            {/* Explanation banner */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                <div className="text-blue-700 font-medium mb-1">Understanding Competitors</div>
                <p className="text-blue-600 text-sm">
                    This section shows the direct competitors in this market segment based on the analysis data. 
                    Analyze their features, pricing, and genres to identify potential differentiators for your game.
                </p>
            </div>

            {/* Market Overview Metrics - Only show if we have competitor data */}
            {sortedCompetitors.length > 0 && (
                <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center">
                        Market Overview
                        <HelpIcon
                            title="Market Overview"
                            description="High-level metrics about the competitive landscape based on analyzed competitor data."
                        />
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {totalMarketRevenue > 0 && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <DollarSign className="w-5 h-5 text-green-600" />
                                    <div className="text-sm text-gray-600">Total Revenue</div>
                                </div>
                                <div className="text-xl font-bold">{formatRevenue(totalMarketRevenue)}</div>
                            </div>
                        )}
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                                <Target className="w-5 h-5 text-blue-600" />
                                <div className="text-sm text-gray-600">Competitors</div>
                            </div>
                            <div className="text-xl font-bold">{sortedCompetitors.length}</div>
                        </div>
                        {averagePrice > 0 && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <DollarSign className="w-5 h-5 text-amber-600" />
                                    <div className="text-sm text-gray-600">Avg Price</div>
                                </div>
                                <div className="text-xl font-bold">${averagePrice.toFixed(2)}</div>
                            </div>
                        )}
                        {landscape.competitionLevel && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <Users className="w-5 h-5 text-purple-600" />
                                    <div className="text-sm text-gray-600">Competition</div>
                                </div>
                                <div className="text-xl font-bold capitalize">
                                    {landscape.competitionLevel}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Direct Competitors */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Direct Competitors
                    <HelpIcon
                        title="Direct Competitors"
                        description="Games that compete directly with your target market, sorted by relevance score when available."
                    />
                </h3>

                {sortedCompetitors.length > 0 ? (
                    <div className="space-y-4">
                        {displayedCompetitors.map((competitor, index) => (
                            <div key={competitor.steamId || index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <button
                                                className="font-medium text-lg text-left hover:text-blue-600"
                                                onClick={() => onGameClick && onGameClick({
                                                    name: competitor.name,
                                                    steamId: competitor.steamId || competitor.id,
                                                    wishlists: competitor.wishlists,
                                                    revenue: competitor.revenue,
                                                    genres: competitor.genres || [],
                                                    tags: competitor.tags || [],
                                                    followers: competitor.followers,
                                                    price: competitor.price
                                                })}
                                            >
                                                {competitor.name || 'Unknown Game'}
                                            </button>
                                            {competitor.marketPosition && (
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    competitor.marketPosition === 'Leader' ? 'bg-green-100 text-green-700' :
                                                    competitor.marketPosition === 'Challenger' ? 'bg-blue-100 text-blue-700' :
                                                    'bg-gray-100 text-gray-700'
                                                }`}>
                                                    {competitor.marketPosition}
                                                </span>
                                            )}
                                        </div>
                                        {competitor.link !== undefined && (
                                            <div className="text-sm text-gray-600 mb-2">
                                                Relevance Score: {(competitor.link * 100).toFixed(1)}%
                                            </div>
                                        )}
                                        {competitor.genres && competitor.genres.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {competitor.genres.map((genre, genreIndex) => (
                                                    <span key={genreIndex} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                                        {genre}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right ml-4">
                                        {competitor.revenue && (
                                            <div className="font-bold text-xl text-green-600 mb-1">
                                                {formatRevenue(competitor.revenue)}
                                            </div>
                                        )}
                                        {competitor.price && (
                                            <div className="text-lg font-medium">${competitor.price}</div>
                                        )}
                                        {competitor.followers && (
                                            <div className="text-sm text-gray-600 mt-1">
                                                {(competitor.followers / 1000).toFixed(0)}K followers
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Additional metrics if available */}
                                {(competitor.wishlists || (competitor.revenue && competitor.price)) && (
                                    <div className="flex justify-between items-center text-sm text-gray-600 pt-2 border-t border-gray-200">
                                        {competitor.wishlists && (
                                            <span>Wishlists: {(competitor.wishlists / 1000).toFixed(0)}K</span>
                                        )}
                                        {competitor.revenue && competitor.price && (
                                            <span>Rev/Price Ratio: {(competitor.revenue / competitor.price / 1000000).toFixed(1)}M</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        {hasMoreCompetitors && (
                            <button
                                onClick={() => setShowAllCompetitors(!showAllCompetitors)}
                                className="w-full py-3 text-blue-600 hover:text-blue-700 text-sm font-medium border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                                {showAllCompetitors 
                                    ? 'Show Less' 
                                    : `Show ${sortedCompetitors.length - 3} More Competitors`
                                }
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="p-6 bg-gray-50 rounded-lg text-center">
                        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Competitor Data Available</h4>
                        <p className="text-gray-600">
                            Competitor analysis data is not available for this analysis. 
                            This could mean the analysis is still processing or no direct competitors were identified.
                        </p>
                    </div>
                )}
            </div>

            {/* Revenue Comparison Chart - Only show if we have revenue data */}
            {displayedCompetitors.length > 0 && displayedCompetitors.some(c => c.revenue) && (
                <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center">
                        Revenue Comparison
                        <HelpIcon
                            title="Revenue Comparison"
                            description="Visual comparison of competitor revenues based on available data."
                        />
                    </h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={displayedCompetitors
                                .filter(c => c.revenue) // Only include competitors with revenue data
                                .map(c => ({
                                    name: c.name && c.name.length > 15 ? c.name.substring(0, 15) + '...' : c.name || 'Unknown',
                                    revenue: c.revenue || 0,
                                    fullName: c.name || 'Unknown Game',
                                    price: c.price || 0
                                }))}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis tickFormatter={formatRevenue} />
                                <Tooltip 
                                    formatter={(value, name, props) => [
                                        formatRevenue(value), 
                                        `Revenue`
                                    ]}
                                    labelFormatter={(label, payload) => {
                                        if (payload && payload[0]) {
                                            const price = payload[0].payload.price;
                                            return `${payload[0].payload.fullName}${price ? ` ($${price})` : ''}`;
                                        }
                                        return label;
                                    }}
                                />
                                <Bar dataKey="revenue" fill="#3B82F6" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Market Opportunities and Challenges */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center">
                        Market Opportunities
                        <HelpIcon
                            title="Market Opportunities"
                            description="Identified gaps and opportunities in the competitive landscape based on the analysis."
                        />
                    </h3>
                    <div className="space-y-3">
                        {opportunities.length > 0 ? (
                            opportunities.map((opportunity, index) => (
                                <div key={index} className="p-3 bg-green-50 text-green-700 rounded-lg">
                                    <div className="text-sm font-medium">
                                        {opportunity.opportunity || opportunity.description || 'Market opportunity identified'}
                                    </div>
                                    {opportunity.impact && (
                                        <div className="text-xs mt-1">
                                            Expected Impact: {opportunity.impact}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="p-4 bg-gray-50 rounded-lg text-center">
                                <div className="text-sm text-gray-600">
                                    No market opportunities identified in the current analysis.
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center">
                        Market Challenges
                        <HelpIcon
                            title="Market Challenges"
                            description="Current market challenges and threats identified in the analysis."
                        />
                    </h3>
                    <div className="space-y-3">
                        {(threats?.immediate || []).length > 0 ? (
                            threats.immediate.map((threat, index) => (
                                <div key={index} className="p-3 bg-red-50 text-red-700 rounded-lg">
                                    <div className="text-sm font-medium">
                                        {threat.threat || 'Market challenge identified'}
                                    </div>
                                    {threat.severity && (
                                        <div className="text-xs mt-1">
                                            Severity: {threat.severity}
                                        </div>
                                    )}
                                    {threat.impact && (
                                        <div className="text-xs mt-1">
                                            Impact: {threat.impact}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="p-4 bg-gray-50 rounded-lg text-center">
                                <div className="text-sm text-gray-600">
                                    No immediate market challenges identified in the current analysis.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Competitive Landscape Summary - Only show if we have landscape data */}
            {landscape && (Object.keys(landscape).length > 0 && (landscape.concentration || landscape.competitionLevel)) && (
                <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center">
                        Competitive Landscape
                        <HelpIcon
                            title="Competitive Landscape"
                            description="Overall analysis of the competitive environment based on available data."
                        />
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {landscape.concentration && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="text-sm text-gray-600 mb-1">Market Concentration</div>
                                <div className="text-xl font-bold capitalize">
                                    {landscape.concentration}
                                </div>
                            </div>
                        )}
                        {landscape.competitionLevel && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="text-sm text-gray-600 mb-1">Competition Level</div>
                                <div className="text-xl font-bold capitalize">
                                    {landscape.competitionLevel}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompetitorsTab;
